import { useEffect, useState } from "react";

/**
 * Tracks the rendered width of an element.
 *
 * ApexCharts only re-measures itself on a `window` resize, so a chart sized at
 * 100% keeps its mount-time width when the dashboard widget around it is dragged
 * to a new size. Feeding the measured width back in as an explicit `width` prop
 * makes react-apexcharts call `updateOptions`, so the chart tracks the widget.
 *
 * Returns a callback ref rather than an object ref because these widgets render a
 * loader first: the measured node only appears once the query resolves, which an
 * effect with an object ref would miss.
 */
export const useContainerWidth = <T extends HTMLElement>() => {
  const [node, setNode] = useState<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      const next = Math.floor(entries[0].contentRect.width);
      setWidth((prev) => (prev === next ? prev : next));
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [setNode, width] as const;
};
